import React, { useEffect, useRef, useState } from "react";

const navigationGroups = [
  {
    id: "solutions",
    label: "SOLUTIONS",
    items: [
      { label: "LLM Gateway", section: "llm-gateway" },
      { label: "Cloud Infrastructure", section: "cloud-infrastructure" },
      { label: "All Products", section: "portfolio" },
      { label: "Customer Cases", section: "testimonials" },
    ],
  },
  {
    id: "company",
    label: "COMPANY",
    items: [
      { label: "About Goldly AI", section: "about" },
      { label: "Global Presence", page: "global-presence" },
      { label: "Our Expertise", page: "team-expertise" },
      { label: "Why Goldly", page: "why-goldly" },
    ],
  },
];

export const Navigation = ({ detailMode, onHome }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const [activeHash, setActiveHash] = useState(window.location.hash);
  const navRef = useRef(null);
  const mobileToggleRef = useRef(null);
  const groupToggleRefs = useRef({});

  const closeMenus = () => {
    setOpenGroup(null);
    setMobileOpen(false);
  };

  useEffect(() => {
    const onHashChange = () => {
      setActiveHash(window.location.hash);
      setOpenGroup(null);
      setMobileOpen(false);
    };
    const onPointerDown = (event) => {
      if (!navRef.current?.contains(event.target)) {
        setOpenGroup(null);
        setMobileOpen(false);
      }
    };
    const desktopQuery = window.matchMedia("(min-width: 768px)");
    const onBreakpointChange = () => {
      setOpenGroup(null);
      setMobileOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("hashchange", onHashChange);
    desktopQuery.addEventListener("change", onBreakpointChange);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("hashchange", onHashChange);
      desktopQuery.removeEventListener("change", onBreakpointChange);
    };
  }, []);

  const handleHome = (event, section = "page-top") => {
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    closeMenus();
    if (onHome) {
      event.preventDefault();
      setActiveHash("#" + section);
      onHome(section);
    } else if (!detailMode) {
      event.preventDefault();
      window.history.replaceState(null, "", "#" + section);
      setActiveHash("#" + section);
      window.requestAnimationFrame(() => {
        document.getElementById(section)?.scrollIntoView({ behavior: "smooth" });
      });
    }
  };

  const handleKeyDown = (event) => {
    if (event.key !== "Escape") return;
    if (openGroup) {
      event.preventDefault();
      groupToggleRefs.current[openGroup]?.focus();
      setOpenGroup(null);
    } else if (mobileOpen) {
      event.preventDefault();
      setMobileOpen(false);
      mobileToggleRef.current?.focus();
    }
  };

  const isActive = (item) => item.page
    ? activeHash === "#/" + item.page || activeHash.startsWith("#/" + item.page + "/")
    : activeHash === "#" + item.section;

  return (
    <nav
      ref={navRef}
      id="menu"
      aria-label="Main navigation"
      onKeyDown={handleKeyDown}
      className={"navbar navbar-default navbar-fixed-top" + (detailMode ? " company-detail-nav" : "")}
    >
      <div className="container">
        <div className="navbar-header">
          <button
            ref={mobileToggleRef}
            type="button"
            className={"navbar-toggle" + (mobileOpen ? "" : " collapsed")}
            aria-expanded={mobileOpen}
            aria-controls="bs-example-navbar-collapse-1"
            aria-label={mobileOpen ? "Close navigation" : "Open navigation"}
            onClick={() => {
              setMobileOpen(!mobileOpen);
              setOpenGroup(null);
            }}
          >
            <span className="icon-bar" />
            <span className="icon-bar" />
            <span className="icon-bar" />
          </button>
          <a
            className="navbar-brand"
            href="#page-top"
            data-scroll-ignore
            aria-label="GOLDLY AI home"
            onClick={(event) => handleHome(event)}
          >
            <img src="img/logo/logo-trim.png" alt="GOLDLY AI" className="navbar-logo" />
          </a>
        </div>

        <div
          className={"collapse navbar-collapse" + (mobileOpen ? " in" : "")}
          id="bs-example-navbar-collapse-1"
        >
          <ul className="nav navbar-nav navbar-right">
            <li className={isActive({ page: "agent-platform" }) ? "active" : ""}>
              <a
                href="#/agent-platform"
                data-scroll-ignore
                aria-current={isActive({ page: "agent-platform" }) ? "page" : undefined}
                onClick={closeMenus}
              >
                AI AGENTS
              </a>
            </li>
            {navigationGroups.map((group) => (
              <React.Fragment key={group.id}>
                <li
                  className={"nav-group" + (group.items.some(isActive) ? " active" : "")}
                  onBlur={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) setOpenGroup(null);
                  }}
                >
                  <button
                    ref={(node) => { groupToggleRefs.current[group.id] = node; }}
                    id={"nav-toggle-" + group.id}
                    type="button"
                    className="nav-group-toggle"
                    aria-expanded={openGroup === group.id}
                    aria-controls={"nav-submenu-" + group.id}
                    onClick={() => setOpenGroup(openGroup === group.id ? null : group.id)}
                    onKeyDown={(event) => {
                      if (event.key !== "ArrowDown") return;
                      event.preventDefault();
                      setOpenGroup(group.id);
                      window.requestAnimationFrame(() => {
                        document.querySelector("#nav-submenu-" + group.id + " a")?.focus();
                      });
                    }}
                  >
                    {group.label}
                    <i className="fa fa-angle-down" aria-hidden="true" />
                  </button>
                  <ul
                    id={"nav-submenu-" + group.id}
                    className="nav-submenu"
                    hidden={openGroup !== group.id}
                    aria-labelledby={"nav-toggle-" + group.id}
                  >
                    {group.items.map((item) => (
                      <li key={item.label}>
                        <a
                          href={item.page ? "#/" + item.page : "#" + item.section}
                          data-scroll-ignore
                          aria-current={isActive(item) ? (item.page ? "page" : "location") : undefined}
                          onClick={item.page ? closeMenus : (event) => handleHome(event, item.section)}
                        >
                          {item.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </li>
                {group.id === "solutions" && (
                  <li className={isActive({ page: "insights" }) ? "active" : ""}>
                    <a
                      href="#/insights"
                      data-scroll-ignore
                      aria-current={isActive({ page: "insights" }) ? "page" : undefined}
                      onClick={closeMenus}
                    >
                      NEWS
                    </a>
                  </li>
                )}
              </React.Fragment>
            ))}
            <li>
              <a href="#contact" data-scroll-ignore onClick={(event) => handleHome(event, "contact")}>
                CONTACT
              </a>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
};
